import React, { useState } from 'react';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  Chat as ChatIcon,
  Event as EventIcon,
  People as DirectoryIcon,
  MoreHoriz as MoreIcon,
  AccountTree as BrowseIcon,
  Poll as SurveyIcon,
  Campaign as AnnouncementIcon,
  Store as MarketplaceIcon,
  VolunteerActivism as FundraiserIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useMarketplaceStatus } from '../../hooks/useMarketplaceStatus';
import { useFundraiserStatus } from '../../hooks/useFundraiserStatus';

interface NavItem {
  label: string;
  value: string;
  icon: React.ReactNode;
}

// A bottom bar only fits a handful of items legibly on a phone. We keep five
// fixed primary tabs (mirroring the native app) and roll everything else into a
// "More" overflow menu so the bar never crams or scrolls regardless of which
// features a tenant has enabled.
const MobileNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled: marketplaceEnabled } = useMarketplaceStatus();
  const { isEnabled: fundraiserEnabled } = useFundraiserStatus();
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  // TODO: Get unread count from context/state
  const unreadCount = 0;

  const primaryItems: NavItem[] = [
    { label: 'Home', value: '/home', icon: <HomeIcon /> },
    {
      label: 'Chats',
      value: '/chats',
      icon:
        unreadCount > 0 ? (
          <Badge badgeContent={unreadCount} color="error">
            <ChatIcon />
          </Badge>
        ) : (
          <ChatIcon />
        ),
    },
    { label: 'Events', value: '/events', icon: <EventIcon /> },
    { label: 'Directory', value: '/directory', icon: <DirectoryIcon /> },
  ];

  const moreItems: NavItem[] = [
    { label: 'Announcements', value: '/announcements', icon: <AnnouncementIcon /> },
    { label: 'Surveys', value: '/surveys', icon: <SurveyIcon /> },
    { label: 'Browse', value: '/browse', icon: <BrowseIcon /> },
    ...(fundraiserEnabled
      ? [{ label: 'Fundraisers', value: '/fundraisers', icon: <FundraiserIcon /> }]
      : []),
    ...(marketplaceEnabled
      ? [{ label: 'Marketplace', value: '/marketplace', icon: <MarketplaceIcon /> }]
      : []),
    { label: 'Settings', value: '/settings', icon: <SettingsIcon /> },
  ];

  const MORE_VALUE = '__more__';

  const isActive = (value: string) =>
    location.pathname === value || location.pathname.startsWith(`${value}/`);

  // Highlight "More" when the current route lives inside the overflow menu.
  const getCurrentValue = () => {
    const matchedPrimary = primaryItems.find(item => isActive(item.value));
    if (matchedPrimary) return matchedPrimary.value;
    if (moreItems.some(item => isActive(item.value))) return MORE_VALUE;
    return false as unknown as string;
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue === MORE_VALUE) return; // handled by the button's onClick
    navigate(newValue);
  };

  const handleMoreNavigate = (value: string) => {
    setMoreAnchor(null);
    navigate(value);
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme => theme.zIndex.appBar,
        borderTop: '1px solid',
        borderColor: 'divider',
        // Respect the iOS home-indicator safe area on phones.
        pb: 'env(safe-area-inset-bottom)',
      }}
      elevation={3}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            px: 0.5,
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.7rem',
            '&.Mui-selected': { fontSize: '0.7rem' },
          },
          '& .Mui-selected': { color: 'primary.main' },
        }}
      >
        {primaryItems.map(item => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
          />
        ))}
        <BottomNavigationAction
          label="More"
          value={MORE_VALUE}
          icon={<MoreIcon />}
          onClick={e => setMoreAnchor(e.currentTarget)}
        />
      </BottomNavigation>

      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {moreItems.map(item => (
          <MenuItem
            key={item.value}
            selected={isActive(item.value)}
            onClick={() => handleMoreNavigate(item.value)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
};

export default MobileNav;
